/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Drawer, Tooltip} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {isRestricted} from '@ion/core/src/entities/LabelRulesValidator';
import {get} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import Avatar from '@ion/dls/src/components/avatar/Avatar';
import CloseButton from '@ion/dls/src/components/buttons/CloseButton';
import {CTALink} from '@ion/dls/src/components/buttons/CTALink';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import LogoutButton from '@ion/dls/src/components/buttons/LogoutButton';
import {
  dlsSidebarDrawerOffset,
  dlsSidebarWidth,
  isSideNavCollapsed
} from '@ion/dls/src/components/navigation/NavigationStore';
import {useSideNavDockedDrawer} from '@ion/dls/src/components/navigation/useSideNavDockedDrawer';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {SystemCallOutlineIcon} from '@ion/dls/src/icons/SystemCallOutlineIcon';
import {SystemEmailIcon} from '@ion/dls/src/icons/SystemEmailIcon';
import Render from '@ion/dls/src/render/Render';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {getSupportDetails, shouldIncludeCalAmpReference, showHelpSection} from '@ion/dls/src/theme/resellers/Resellers';
import {dlsAlt, dlsColor, dlsShadow, dlsTypography, dlsZIndex} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {AvatarIconSize, IconSize} from '@ion/dls/src/types/types';
import {editAdminUser} from '@/store/actions/admin-users';
import {isPreInitializedSelector} from '@/store/selectors/admin-users-selectors';
import {globalGroupTitleSelector} from '@/store/selectors/global-group-chooser-selectors';
import {
  isAuthenticatingSelector,
  userSelector
} from '@/store/selectors/login-selectors';
import {useLogout} from '../hooks/useLogout';
import {hasAppHelpAccess} from '../security/Features';

export interface DesktopUserProfileViewType {
  onLogout?: () => void;
  handleClick: Function;
  onContactSupportOpen: () => void;
  open: boolean;
  id: string;
  anchorEl: any;
  isLoggingOut?: boolean;
  username: string;
  disabled?: boolean;
  title?: string;
}

const useStyles = makeStyles(theme => ({
  profileHeader : toggleThemeStyle({
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    borderBottom   : `solid 1px ${dlsColor('grey5')}`
  }, {
    gap                            : '10px',
    height                         : '56px',
    display                        : 'flex',
    alignItems                     : 'center',
    justifyContent                 : 'space-between',
    padding                        : '0 16px 0 20px',
    borderBottom                   : `solid 1px ${dlsColor('grey5')}`,
    [theme.breakpoints.down('sm')] : {
      margin : '0 -15px'
    }
  }),
  profileTitle : toggleThemeStyle({
    textTransform : 'uppercase',
    fontWeight    : 500,
    letterSpacing : '1px'
  }, {
    color         : dlsColor('grey1'),
    fontSize      : '16px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '20px',
    letterSpacing : '0px'
  }),
  popup : {
    border          : `1px solid ${dlsColor('grey5')}`,
    boxSizing       : 'border-box',
    boxShadow       : dlsAlt('components', 'boxShadow', 'shadow4'),
    backgroundColor : dlsColor('white')
  },
  ionLink : {
    ...dlsAlt('components', 'ionLinkStyle', 'ionLinkAlt')
  },
  user : toggleThemeStyle({
    display    : 'block',
    fontSize   : '16px',
    lineHeight : '20px',
    color      : ({enableProfile}: {enableProfile: boolean}) => enableProfile ? dlsColor('grey1') : dlsColor('grey5'),
    fontWeight : 500
  }, {
    color         : dlsColor('grey1'),
    fontSize      : '16px',
    fontFamily    : 'Inter',
    fontWeight    : 500,
    lineHeight    : '20px',
    letterSpacing : '0px'
  }),
  options : toggleThemeStyle({
    fontSize    : '14px',
    lineHeight  : '20px',
    color       : dlsColor('grey2'),
    fontWeight  : 400,
    cursor      : 'pointer',
    marginLeft  : 20,
    marginRight : 20
  }, {
    color         : dlsColor('grey1'),
    cursor        : 'pointer',
    fontSize      : '14px',
    fontFamily    : 'Inter',
    fontWeight    : 500,
    lineHeight    : '16px',
    marginLeft    : 20,
    marginRight   : 20,
    letterSpacing : '0px'
  }),
  disabled : {
    color         : dlsColor('grey4'),
    cursor        : 'pointer',
    letterSpacing : '1px',
    ...dlsTypography('body3')
  },
  account : toggleThemeStyle({
    lineHeight : '20px',
    fontSize   : '14px',
    color      : dlsColor('grey3'),
    fontWeight : 400
  }, {
    color         : dlsColor('grey3'),
    fontSize      : '14px',
    fontFamily    : 'Inter',
    fontWeight    : 400,
    lineHeight    : '20px',
    letterSpacing : '0px'
  }),
  container : toggleThemeStyle({
    width           : 400,
    backgroundColor : dlsColor('white')
  }, {
    width           : 456,
    height          : '100%',
    display         : 'flex',
    flexDirection   : 'column',
    backgroundColor : dlsColor('white')
  }),
  userWrapper : {
    width  : '100%',
    margin : '12px 0'
  },
  margin : {
    marginLeft  : 20,
    marginRight : 20
  },
  subContainer : {
    width       : '100%',
    alignItems  : 'center',
    display     : 'flex',
    marginLeft  : 20,
    marginRight : 20
  },
  optionWrapperContainer : {
    display       : 'flex',
    flexDirection : 'column'
  },
  optionWrapper : {
    width      : '100%',
    height     : 48,
    alignItems : 'center',
    display    : 'flex',
    '&:hover'  : {
      backgroundColor : dlsColor('paleBlue')
    }
  },
  previewWrapper : {
    display        : 'flex',
    width          : '100%',
    justifyContent : 'space-between'
  },
  switch : {
    display    : 'flex',
    alignItems : 'center'
  },
  drawerRoot : {
    marginLeft : '-20px',
    zIndex     : `${dlsZIndex('desktopUserProfileViewPopover')} !important` as any
  },
  drawerPaper : {
    left                           : ({collapsed}: {collapsed: boolean}) => dlsSidebarDrawerOffset(collapsed),
    right                          : 'auto !important',
    overflow                       : 'hidden',
    boxShadow                      : dlsShadow(1),
    borderRadius                   : toggleThemeStyle('12px', '0 12px 12px 0'),
    borderLeft                     : toggleThemeStyle('none', `1px solid ${dlsColor('pastelTeal')}`),
    [theme.breakpoints.down('sm')] : {
      width  : 'auto',
      height : '100vh'
    }
  },
  closeIcon : {
    position  : 'absolute',
    marginTop : '5px',
    left      : '12px'
  },
  logOutWrapper : {
    display : toggleThemeStyle('flex', 'block')
  },
  linksSection : {
    padding : '12px 20px 4px'
  },
  linkRow : {
    width           : '100%',
    minHeight       : '44px',
    display         : 'flex',
    flexDirection   : 'column',
    justifyContent  : 'center',
    padding         : 0,
    border          : 0,
    backgroundColor : 'transparent',
    font            : 'inherit',
    textAlign       : 'left',
    cursor          : 'pointer'
  },
  linkTitle : {
    color      : dlsColor('linkBlue'),
    fontFamily : 'Inter',
    fontSize   : '14px',
    fontWeight : 500,
    lineHeight : '20px'
  },
  linkSubtitle : {
    color      : dlsColor('grey3'),
    fontFamily : 'Inter',
    fontSize   : '12px',
    fontWeight : 400,
    lineHeight : '16px'
  },
  divider : {
    borderBottom : `solid 1px ${dlsColor('grey5')}`,
    margin       : '4px 20px 0'
  },
  contactSectionLabel : {
    color         : dlsColor('grey3'),
    fontFamily    : 'Inter',
    fontSize      : '11px',
    fontWeight    : 600,
    letterSpacing : '0.5px',
    textTransform : 'uppercase',
    padding       : '16px 20px 8px'
  },
  contactRow : {
    minHeight  : '44px',
    display    : 'flex',
    alignItems : 'center',
    gap        : '8px',
    padding    : '0 20px',
    cursor     : 'pointer'
  },
  spacer : {
    flex : 1
  },
  footer : {
    padding       : '16px 20px',
    display       : 'flex',
    flexDirection : 'column',
    gap           : '12px',
    borderTop     : `solid 1px ${dlsColor('grey5')}`
  },
  footerUserRow : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '10px'
  },
  footerUserInfo : {
    display       : 'flex',
    flexDirection : 'column',
    minWidth      : 0
  },
  footerUserEmail : {
    color        : dlsColor('grey1'),
    fontFamily   : 'Inter',
    fontSize     : '14px',
    fontWeight   : 400,
    lineHeight   : '20px',
    overflow     : 'hidden',
    textOverflow : 'ellipsis',
    whiteSpace   : 'nowrap'
  },
  footerUserGroup : {
    color        : dlsColor('darkTeal'),
    fontFamily   : 'Inter',
    fontSize     : '12px',
    fontWeight   : 400,
    lineHeight   : '16px',
    overflow     : 'hidden',
    textOverflow : 'ellipsis',
    whiteSpace   : 'nowrap'
  }
}));

const iconsTooltip = makeStyles((theme) => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'iconTooltip')
  }
}));

function IconCustomTooltip(props) {
  return <Tooltip arrow classes={iconsTooltip()} {...props} />;
}

/**
 * Launches a separate tab to display the CalAmp privacy policy page.
 *
 * ```typescript
 *   import {viewPrivacyPolicy} from '@ion/ion-suite/src/components/header/DesktopProfileView';
 *
 *   viewPrivacyPolicy(closeMenuFunc);
 *
 * ```
 *
 * @param  postOpenFunc An optional function to execute following the launch of the
 *         privacy policy page (ex. function to close the menu from which the launch
 *         was initiated).
 */
export const viewPrivacyPolicy = function(postOpenFunc?: Function) {
  return function() {
    window.open(get('privacyPolicyURL'), '_blank');
    postOpenFunc?.();
  };
};

/**
 * Launches a separate tab to display the CalAmp Help page.
 *
 * ```typescript
 *   import {viewHelp} from '@ion/ion-suite/src/components/header/DesktopProfileView';
 *
 *   viewHelp(closeMenuFunc);
 *
 * ```
 *
 * @param  postOpenFunc An optional function to execute following the launch of the
 *         Help page (ex. function to close the menu from which the launch
 *         was initiated).
 */
export const viewHelp = function(postOpenFunc?: Function) {
  return function() {
    window.open(get('helpDocumentURL'), '_blank');
    postOpenFunc?.();
  };
};

/**
 * @DesktopUserProfileView
 *
 * The Desktop user profile view  component provides the application user profile view
 * logout option and
 * ion preview.
 */
export const DesktopUserProfileView = ({
  handleClick,
  open,
  id,
  anchorEl,
  username,
  title = translate('nav:ionprime.profileSettings'),
  onContactSupportOpen
}: DesktopUserProfileViewType) => {
  const dispatch      = useDispatch();
  const enableProfile = useSelector(isPreInitializedSelector);
  const accountName   = useSelector(globalGroupTitleSelector);
  const currentUser   = useSelector(userSelector);
  const isLoggingOut  = useSelector(isAuthenticatingSelector);
  const collapsed     = isSideNavCollapsed.value;
  const classes       = useStyles({enableProfile, collapsed});
  const onLogout      = useLogout();
  const {isPhone}     = useMedia();
  const currentRole   = EntityLabel.getRole();
  const canAccessHelp = hasAppHelpAccess();

  const {
    isLegacy  : isLegacyTheme,
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const onContactSupport = useCallback(function() {
    handleClick();
    onContactSupportOpen();
  }, [handleClick, onContactSupportOpen]);

  const {email, phone} = getSupportDetails();
  const backdropOffset = dlsSidebarWidth(collapsed);
  const dockedDrawer   = useSideNavDockedDrawer(backdropOffset);

  const onEditProfile = useCallback(function() {
    handleClick();
    dispatch(editAdminUser(currentUser, true) as any);
  }, [handleClick, dispatch, currentUser]);

  return (
    <>
      <Drawer anchor={isCurrentTheme ? 'left' : 'right'} id={id}
        classes={{
          root : classes.drawerRoot,
          ...(isCurrentTheme && {
            paper : classes.drawerPaper
          })
        }}
        {...(isCurrentTheme && {
          BackdropProps : {
            className : dockedDrawer.backdropClassName,
            style     : {
              left            : backdropOffset,
              backgroundColor : 'rgba(0, 0, 0, 0.3)'
            }
          }
        })}
        className={dockedDrawer.className}
        disableEnforceFocus={dockedDrawer.disableEnforceFocus}
        open={open}
        onClose={function(e) { handleClick(e); }}>
        <div className={classes.container}>

          {
            !isPhone && (
              <div className={classes.profileHeader}>
                <Render.If condition={isLegacyTheme}>
                  <div className={classes.closeIcon}>
                    <IconButton icon={<MaterialExitCloseIcon size={IconSize.medium} color={dlsColor('grey3')} />}
                      onClick={handleClick} />
                  </div>
                </Render.If>
                <h2 className={classes.profileTitle}>{title}</h2>
                <Render.If condition={isCurrentTheme}>
                  <CloseButton onClick={handleClick} />
                </Render.If>
              </div>
            )
          }
          <Render.If condition={isLegacyTheme}>
            <div className={classes.userWrapper}>
              <div className={classes.subContainer}>
                <div>
                  <Avatar notClickable name={username} />
                </div>
                <div style={{marginLeft: '12px', display: 'flex', whiteSpace: 'pre'}}>
                  <label className={classes.user}>{username}</label>
                  {' - '}
                  <label className={classes.account}>{isRestricted(currentRole) ? entityId(currentUser?.account?.href)
                    : currentUser?.account?.title}</label>
                </div>
              </div>
            </div>
            <div className={classes.optionWrapperContainer}>
              <div className={classes.optionWrapper}>
                <CTALink className={classes.options} onClick={enableProfile ? onEditProfile : undefined}>
                  {translate('nav:ionprime.profile')}
                </CTALink>
              </div>
              <Render.If condition={showHelpSection() && canAccessHelp}>
                <div className={classes.optionWrapper}>
                  <CTALink className={classes.options}
                    onClick={viewHelp(handleClick)}>
                    {translate('nav:ionprime.help')}
                  </CTALink>
                </div>
              </Render.If>
              <div className={classes.optionWrapper}>
                <CTALink className={classes.options}
                  onClick={onContactSupport}>
                  {translate('nav:ionprime.contactSupport')}
                </CTALink>
              </div>
              <Render.If condition={shouldIncludeCalAmpReference()}>
                <div className={classes.optionWrapper}>
                  <CTALink className={classes.options}
                    onClick={viewPrivacyPolicy(handleClick)}>
                    {translate('nav:ionprime.privacyPolicy')}
                  </CTALink>
                </div>
              </Render.If>
            </div>
            <div className={classes.logOutWrapper}>
              <LogoutButton label={translate('nav:ionprime.logout')}
                isLoggingOut={isLoggingOut}
                onLogout={onLogout}
                iconOnly={false} />
            </div>
          </Render.If>
          <Render.If condition={isCurrentTheme}>
            <div className={classes.linksSection}>
              <button type='button' className={classes.linkRow}
                disabled={!enableProfile}
                onClick={onEditProfile}>
                <span className={classes.linkTitle}>{translate('nav:ionprime.profile')}</span>
                <span className={classes.linkSubtitle}>{translate('nav:ionprime.profileSubtitle')}</span>
              </button>
              <Render.If condition={shouldIncludeCalAmpReference()}>
                <button type='button' className={classes.linkRow}
                  onClick={viewPrivacyPolicy(handleClick)}>
                  <span className={classes.linkTitle}>{translate('nav:ionprime.privacyPolicy')}</span>
                  <span className={classes.linkSubtitle}>{translate('nav:ionprime.privacyPolicySubtitle')}</span>
                </button>
              </Render.If>
            </div>
            <div className={classes.divider} />
            <div className={classes.contactSectionLabel}>
              {translate('nav:ionprime.contactSupport')}
            </div>
            <Render.If condition={!!email}>
              <a className={classes.contactRow} href={`mailto:${email}`}>
                <SystemEmailIcon size={IconSize.small} color={dlsColor('grey3')} />
                <span className={classes.linkTitle}>{email}</span>
              </a>
            </Render.If>
            <Render.If condition={!!phone}>
              <a className={classes.contactRow} href={`tel:${phone}`}>
                <SystemCallOutlineIcon size={IconSize.small} color={dlsColor('grey3')} />
                <span className={classes.linkTitle}>{phone}</span>
              </a>
            </Render.If>
            <div className={classes.spacer} />
            <div className={classes.footer}>
              <div className={classes.footerUserRow}>
                <Avatar notClickable name={username} size={AvatarIconSize.small} />
                <div className={classes.footerUserInfo}>
                  <span className={classes.footerUserEmail}>{username}</span>
                  <span className={classes.footerUserGroup}>
                    {isRestricted(currentRole) ? entityId(currentUser?.account?.href) : currentUser?.account?.title}
                  </span>
                </div>
              </div>
              <LogoutButton label={translate('nav:ionprime.logout')}
                isLoggingOut={isLoggingOut}
                onLogout={onLogout}
                iconOnly={false} />
            </div>
          </Render.If>
        </div>
      </Drawer>
    </>
  );
};

export default DesktopUserProfileView;
